import { useEffect } from 'react';
import { useMotionValue } from 'framer-motion';

// One native-scroll observer for the whole page. No cached target offsets:
// expanding archives, filtering grids and image loads all change page geometry.
const listeners = new Set();
let pendingFrame = 0;
let resizeObserver;
const schedule = () => {
  if (pendingFrame) return;
  pendingFrame = window.requestAnimationFrame(() => {
    pendingFrame = 0;
    const viewport = { scroll: window.scrollY, height: window.innerHeight, width: window.innerWidth };
    listeners.forEach(listener => listener(viewport));
  });
};

export const observeDocumentScroll = listener => {
  listeners.add(listener);
  if (listeners.size === 1) {
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('load', schedule, true);
    resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(document.body);
    document.fonts?.ready.then(schedule);
  }
  schedule();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('load', schedule, true);
      resizeObserver?.disconnect();
      window.cancelAnimationFrame(pendingFrame);
      pendingFrame = 0;
    }
  };
};

const layoutTop = element => {
  let top = 0;
  for (let node = element; node; node = node.offsetParent) top += node.offsetTop || 0;
  return top;
};

export const useCardScroll = (target, reduced, row) => {
  const y = useMotionValue(0);
  const rotateX = useMotionValue(0);
  useEffect(() => {
    if (reduced) {
      y.set(0); rotateX.set(0);
      if (target.current) target.current.dataset.scrollProgress = 'reduced';
      return;
    }
    return observeDocumentScroll(viewport => {
      const element = target.current;
      if (!element || !element.offsetHeight) return;
      const raw = (viewport.scroll + viewport.height - layoutTop(element)) / (viewport.height + element.offsetHeight);
      const progress = Math.max(0, Math.min(1, raw));
      const distance = row ? 32 : viewport.width <= 640 ? 66 : 100;
      y.set(distance * (1 - 2 * progress));
      rotateX.set((row ? 4 : 11) * (1 - 2 * progress));
      element.dataset.scrollProgress = progress.toFixed(4);
    });
  }, [target, reduced, row, y, rotateX]);
  return { y, rotateX };
};